import { useState } from "preact/hooks";
import { route } from "preact-router";
import type { RoutableProps } from "preact-router";
import { fetchInvitation, submitReview } from "../api";
import { BrandHeader } from "../components/brandHeader";
import { PrimaryButton } from "../components/buttons";
import { LegalFooter } from "../components/footer";
import { HoneypotInput } from "../components/honeypotInput";
import { Checkbox, TextInput } from "../components/inputs";
import { RatingsCard } from "../components/ratingsCard";
import { ReviewTextCard } from "../components/reviewTextCard";
import { STRINGS } from "../constants/strings";
import { useFormState } from "../hooks/useFormState";
import { useResourceLoad } from "../hooks/useResourceLoad";
import { toErrorPath, toPendingPath } from "../routes";
import type { ErrorType } from "../types";
import { isValidEmail } from "../utils/validation";
import { handleSubmitFailure } from "../utils/submitFailure";

interface Props extends RoutableProps {
  linkToken?: string;
}

const REDIRECT_STATUSES: ErrorType[] = ["expired", "used", "invalid"];

export function ReviewFormScreen({ linkToken }: Props) {
  const { data: invitation, loadError } = useResourceLoad(
    linkToken,
    fetchInvitation
  );
  const form = useFormState();
  const [emailBlurred, setEmailBlurred] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState("");

  if (!invitation) {
    return (
      <div class="tl-page">
        <div class="tl-page-centered">
          {loadError ? (
            <span class="tl-field__error" role="alert">
              {loadError}
            </span>
          ) : (
            <p>{STRINGS.loading}</p>
          )}
        </div>
      </div>
    );
  }

  const companyName = invitation.companyName;
  const email = form.state.reviewer.email;
  const emailError = emailBlurred
    ? !email
      ? STRINGS.validationEmailMissing
      : !isValidEmail(email)
        ? STRINGS.validationEmailInvalid
        : undefined
    : undefined;

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (!form.submitEnabled || submitting || !linkToken) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submitReview({
        linkToken,
        ratings: form.state.ratings,
        freitext: form.state.freitext,
        reviewer: form.state.reviewer,
        consent: form.state.consent,
        displayConsent: form.state.displayConsent,
        anonymous: form.state.anonymous,
        website_url: websiteUrl,
      });
      if (res.status === "ok") {
        sessionStorage.setItem(
          `review:${linkToken}`,
          JSON.stringify({
            pendingId: res.pendingId,
            email: form.state.reviewer.email,
          })
        );
        route(toPendingPath(linkToken));
        return;
      }
      if (res.status === "duplicate") {
        route(toErrorPath("duplicate", { companyName }));
        return;
      }
      handleSubmitFailure(res, REDIRECT_STATUSES, setSubmitError);
    } catch {
      setSubmitError(STRINGS.genericError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div class="tl-page">
      <BrandHeader
        companyName={companyName}
        companyLogoUrl={invitation.companyLogoUrl}
      />
      <form class="tl-page-main" onSubmit={onSubmit}>
        <RatingsCard
          companyName={companyName}
          ratings={form.state.ratings}
          onChange={form.setRating}
        />

        <ReviewTextCard value={form.state.freitext} onInput={form.setFreitext} />

        <div class="tl-card">
          <h3 class="tl-card__heading">{STRINGS.contactSectionTitle}</h3>
          <div class="tl-form-stack">
            <div class="tl-name-grid">
              <TextInput
                label={STRINGS.firstNameLabel}
                value={form.state.reviewer.firstName}
                placeholder={STRINGS.firstNamePlaceholder}
                optional
                onInput={(v) => form.setReviewerField("firstName", v)}
              />
              <TextInput
                label={STRINGS.lastNameLabel}
                value={form.state.reviewer.lastName}
                placeholder={STRINGS.lastNamePlaceholder}
                optional
                onInput={(v) => form.setReviewerField("lastName", v)}
              />
            </div>
            <TextInput
              label={STRINGS.emailLabel}
              value={email}
              type="email"
              placeholder={STRINGS.emailPlaceholder}
              required
              error={emailError}
              onInput={(v) => form.setReviewerField("email", v)}
              onBlur={() => setEmailBlurred(true)}
            />
            <div class="tl-info-card">
              <span class="material-icons" aria-hidden="true">
                lock
              </span>
              <span>{STRINGS.emailInfo}</span>
            </div>
            <div>
              <Checkbox
                label={STRINGS.anonymousLabel}
                checked={form.state.anonymous}
                ariaDescribedBy="anonymous-hint"
                onChange={form.setAnonymous}
              />
              <p id="anonymous-hint" class="tl-checkbox-hint">
                {STRINGS.anonymousHint}
              </p>
            </div>
          </div>
        </div>

        <div class="tl-card">
          <div class="tl-form-stack">
            <Checkbox
              label={STRINGS.consentStorageLabel}
              required
              checked={form.state.consent}
              onChange={form.setConsent}
            />
            <Checkbox
              label={STRINGS.consentPublicLabel}
              checked={form.state.displayConsent}
              onChange={form.setDisplayConsent}
            />
          </div>
        </div>

        <HoneypotInput value={websiteUrl} onInput={setWebsiteUrl} />

        {submitError && (
          <div class="tl-field__error" role="alert">
            {submitError}
          </div>
        )}

        <div class="tl-submit-row">
          <span class="tl-required-hint">{STRINGS.requiredHint}</span>
          <PrimaryButton
            text={STRINGS.submit}
            icon="send"
            type="submit"
            disabled={!form.submitEnabled}
            loading={submitting}
          />
        </div>

        <details class="tl-authenticity">
          <summary>{STRINGS.authenticityTitle}</summary>
          <p>{STRINGS.authenticityNotice}</p>
        </details>
      </form>
      <LegalFooter />
    </div>
  );
}
